import { City } from '../types';

type WikimediaImage = string | undefined;

const wikimediaImageCache = new Map<string, WikimediaImage>();
const wikimediaImageRequests = new Map<string, Promise<WikimediaImage>>();

function getCityCacheKey(city: City): string | null {
  if (typeof city.lat !== 'number' || typeof city.lon !== 'number') return null;
  return `${city.lat.toFixed(5)},${city.lon.toFixed(5)}`;
}

async function fetchNearestCommonsImage(lat: number, lon: number): Promise<WikimediaImage> {
  const api = new URL('https://commons.wikimedia.org/w/api.php');
  api.searchParams.set('action', 'query');
  api.searchParams.set('format', 'json');
  api.searchParams.set('origin', '*');
  api.searchParams.set('generator', 'geosearch');
  api.searchParams.set('ggscoord', `${lat}|${lon}`);
  api.searchParams.set('ggsradius', '10000');
  api.searchParams.set('ggslimit', '1');
  api.searchParams.set('prop', 'pageimages');
  api.searchParams.set('piprop', 'original|thumbnail');
  api.searchParams.set('pithumbsize', '900');

  const response = await fetch(api.toString());
  if (!response.ok) return undefined;
  const data = (await response.json()) as {
    query?: {
      pages?: Record<
        string,
        {
          original?: { source?: string };
          thumbnail?: { source?: string };
        }
      >;
    };
  };

  const pages = data.query?.pages;
  if (!pages) return undefined;
  const page = Object.values(pages)[0];
  return page?.original?.source ?? page?.thumbnail?.source;
}

/**
 * Picks the best image available for a city, favoring curated images when provided and
 * falling back to the nearest Wikimedia Commons image for the city's location.
 */
export async function getCityImage(city: City): Promise<WikimediaImage> {
  if (city.image_url) return city.image_url;

  const cacheKey = getCityCacheKey(city);
  if (!cacheKey) return undefined;

  const cached = wikimediaImageCache.get(cacheKey);
  if (cached !== undefined || wikimediaImageCache.has(cacheKey)) return cached;

  const pending = wikimediaImageRequests.get(cacheKey);
  if (pending) return pending;

  const request = fetchNearestCommonsImage(city.lat, city.lon)
    .then((image) => {
      wikimediaImageCache.set(cacheKey, image);
      wikimediaImageRequests.delete(cacheKey);
      return image;
    })
    .catch(() => {
      wikimediaImageCache.set(cacheKey, undefined);
      wikimediaImageRequests.delete(cacheKey);
      return undefined;
    });

  wikimediaImageRequests.set(cacheKey, request);
  return request;
}
